import { HStack, VStack } from "@gluestack-ui/themed";
import { Badge } from "@repo/core/components/core/Badge";
import { Button } from "@repo/core/components/core/Button";
import { Text } from "@repo/core/components/core/Text";
import { CardCollapse } from "@repo/core/components/layout/CardCollapse";
import {
  StatusBadge,
  ProposalStatusDescriptions
} from "@repo/core/constants/proposals";
import {
  ProposalCardStatusEnum,
  ProposalCardTypeLabelEnum
} from "@repo/core/enums/proposals";
import { LocationListTile } from "@repo/core/screens/Proposals/components/LocationListTile";
import { ProductListTile } from "@repo/core/screens/Proposals/components/ProductListTile";
import { ProposalDetailsProps } from "./types";
import { CardCollapseVariant } from "@repo/core/components/layout/CardCollapse/types";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { AnalysisParecerView } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/components/AnalysisParecerView";
import { CardStatus } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/components/CardStatus";
import { canViewProposalLimits } from "@repo/core/screens/Proposals/screens/ProposalsViewInfo/utils";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import { normalizeProposalSituationCode } from "@repo/core/screens/Proposals/rules/analysisQueueRules";

const REJECTED_STATUSES: string[] = [
  ProposalCardStatusEnum.REJECTED,
  ProposalCardStatusEnum.REJECTED_CREDIT_DESK
];

/**
 * Gets the status badge text, prioritizing API description when available
 */
const getStatusBadgeText = (
  status: ProposalCardStatusEnum | string | undefined,
  statusCode?: string,
  statusDescription?: string
): string => {
  // Normalize status code to always have 2 digits
  const normalizedCode =
    statusCode && statusCode.trim() !== ""
      ? statusCode.length === 1
        ? `0${statusCode}`
        : statusCode
      : null;

  // Priority 1: Use API description if available
  if (normalizedCode && statusDescription && statusDescription.trim() !== "") {
    return `${normalizedCode} - ${statusDescription}`;
  }

  // Priority 2: Use fallback mapping if we have status code
  if (normalizedCode && ProposalStatusDescriptions[normalizedCode]) {
    return `${normalizedCode} - ${ProposalStatusDescriptions[normalizedCode]}`;
  }

  // Priority 3: Fallback to current system
  if (status) {
    return (
      StatusBadge[status as ProposalCardStatusEnum]?.text ||
      StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].text
    );
  }
  return StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].text;
};

export const ProposalDetails = ({
  showAnalytics,
  showAnalyticsDetails = true,
  proposalAnalytics,
  standardRejectionReasons,
  onSeeLimits,
  clientName,
  cpf,
  proposalDate,
  productCode,
  productDescription,
  pointOfService,
  status,
  statusCode,
  statusDescription,
  proposalType
}: ProposalDetailsProps) => {
  const { isMobile } = useDimensions();

  const isRejected = !!status && REJECTED_STATUSES.includes(status);
  const normalizedStatusCode = normalizeProposalSituationCode(statusCode);
  const isRejectedByCreditDesk =
    normalizedStatusCode === PROPOSTA_SITUACAO.REJECTED_MANUALLY;
  const shouldShowRejectedCreditWarning =
    isRejected &&
    normalizedStatusCode !== PROPOSTA_SITUACAO.MANUAL_ANALYSIS_QUEUE;
  const shouldShowLimits =
    !isRejected && canViewProposalLimits(statusCode, proposalType);

  return (
    <VStack gap="$6" w="$full" alignSelf="stretch">
      {shouldShowRejectedCreditWarning && (
        <CardStatus
          title="Crédito não disponível, no momento."
          titleColor="$error700"
        >
          <Text
            fontSize="$sm"
            color="$textLight900"
            fontWeight={400}
            textAlign="center"
          >
            Não foi possível a aprovação automática. Você pode prosseguir com a{" "}
            <Text fontSize="$sm" color="$primary700" fontWeight={600}>
              Efetivação do Fidelidade
            </Text>{" "}
            para pontuar nesta venda
            {isRejectedByCreditDesk ? (
              "."
            ) : (
              <>
                {" "}
                ou solicitar o apoio da{" "}
                <Text fontSize="$sm" color="$primary700" fontWeight={600}>
                  Mesa de Crédito
                </Text>
                .
              </>
            )}
          </Text>
        </CardStatus>
      )}
      <HStack gap="$2">
        <VStack flex={1} gap="$2">
          <VStack gap="$2">
            <Text fontSize="$xs" color="$textLight700" fontWeight={500}>
              Status da proposta
            </Text>
            <Badge
              type={
                status
                  ? StatusBadge[status as ProposalCardStatusEnum]?.type ||
                    StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].type
                  : StatusBadge[ProposalCardStatusEnum.IN_PROGRESS].type
              }
              text={getStatusBadgeText(
                status as ProposalCardStatusEnum,
                statusCode,
                statusDescription
              )}
            />
          </VStack>
        </VStack>
        {shouldShowLimits && (
          <VStack flex={1} alignItems={isMobile ? "flex-start" : "flex-end"}>
            <VStack flex={1} gap="$2" width={isMobile ? "$full" : "auto"}>
              <Text fontSize="$xs" color="$textLight700" fontWeight={500}>
                Limites de Crédito
              </Text>
              <Button
                text="Ver Limites"
                size="sm"
                onPress={onSeeLimits}
                leftIcon={{
                  name: "bars-progress",
                  size: 16
                }}
                action="darker-secondary"
                variant="outline"
              />
            </VStack>
          </VStack>
        )}
      </HStack>
      {showAnalytics &&
        standardRejectionReasons &&
        standardRejectionReasons.length > 0 && (
          <VStack
            borderWidth={1}
            borderColor="$error400"
            borderRadius="$lg"
            bgColor="$error50"
            p="$4"
            gap="$3"
          >
            <Text fontSize="$sm" fontWeight={600} color="$error700">
              Motivo da recusa
            </Text>
            <VStack gap="$2">
              {standardRejectionReasons.map((reason) => (
                <Text
                  key={reason}
                  fontSize="$xs"
                  color="$error800"
                  lineHeight="$md"
                >
                  {reason}
                </Text>
              ))}
            </VStack>
          </VStack>
        )}
      {showAnalytics && showAnalyticsDetails ? (
        <VStack gap="$2">
          <Text fontSize="$xs" fontWeight={500} color="$textLight800">
            Itens Analisados
          </Text>
          {!proposalAnalytics || proposalAnalytics.length === 0 ? (
            <VStack alignItems="center" justifyContent="center" py="$8" px="$4">
              <Text fontSize="$sm" color="$textLight600" textAlign="center">
                Nenhum dado analisado encontrado.
              </Text>
            </VStack>
          ) : (
            <VStack gap="$2">
              {proposalAnalytics.map((item, index) => {
                // Get status text based on situacao (collapsed row)
                const statusText =
                  item.situacao === "A"
                    ? "Aprovado."
                    : item.situacao === "R"
                      ? "Reprovado."
                      : item.situacao === "P"
                        ? "Pendente."
                        : "";

                return (
                  <CardCollapse
                    key={index}
                    title={item.title}
                    subtitle={statusText}
                    variant={item.variant as CardCollapseVariant}
                    isLoading={item.isLoading}
                  >
                    {item.subtitle?.trim() &&
                    item.subtitle.trim() !== statusText.trim() ? (
                      <VStack px="$4" pb="$2">
                        <AnalysisParecerView parecer={item.subtitle} />
                      </VStack>
                    ) : null}
                  </CardCollapse>
                );
              })}
            </VStack>
          )}
        </VStack>
      ) : (
        <>
          <VStack gap="$3">
            <VStack flex={1} gap="$1">
              <Text fontSize="$xs" fontWeight={500} color="$textLight700">
                Nome
              </Text>
              <Text fontSize="$md" fontWeight={400} color="$textLight900">
                {clientName || "-"}
              </Text>
            </VStack>
            <VStack flex={1} gap="$1">
              <Text fontSize="$xs" fontWeight={500} color="$textLight700">
                CPF
              </Text>
              <Text fontSize="$md" fontWeight={400} color="$textLight900">
                {cpf || "-"}
              </Text>
            </VStack>
            <VStack flex={1} gap="$1">
              <Text fontSize="$xs" fontWeight={500} color="$textLight700">
                Data da Proposta
              </Text>
              <Text fontSize="$md" fontWeight={400} color="$textLight900">
                {proposalDate || "-"}
              </Text>
            </VStack>
            {/*  <VStack flex={1} mt={!isMobile ? "$0" : "$2"}>
              <Button
                text="Ver mais"
                size="sm"
                onPress={onSeeMore}
                leftIcon={{
                  name: "file",
                  size: 16
                }}
                action="darker-secondary"
                variant="outline"
              />
            </VStack> */}
          </VStack>
          <VStack gap="$1">
            <Text fontSize="$xs" fontWeight={500} color="$textLight700">
              Produto
            </Text>
            <ProductListTile
              name={productDescription || "-"}
              id={productCode || "1"}
              type={ProposalCardTypeLabelEnum.FIDELITY_CREDIT}
              number={productCode || ""}
            />
          </VStack>
          <VStack gap="$1">
            <Text fontSize="$xs" fontWeight={500} color="$textLight700">
              Ponto de Atendimento
            </Text>
            <LocationListTile
              id="1"
              name={pointOfService || "-"}
              region=""
              address=""
            />
          </VStack>
        </>
      )}
    </VStack>
  );
};
